import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/safeStorage';
import { User } from '../types';
import api from '../services/api';
import logger from '../utils/logger';

export interface TwoFARequired {
  requires_2fa: true;
  temp_token: string;
  two_fa_method: 'webauthn' | 'email_otp';
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string, tenantId?: number | null, tenantSlug?: string | null) => Promise<TwoFARequired | void>;
  complete2FALogin: (accessToken: string, refreshToken: string, deviceToken?: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Validate token with server on app load instead of trusting localStorage
    const loadUser = async () => {
      const token = safeGetItem('access_token');

      if (token) {
        try {
          // Validate token with server — prevents localStorage manipulation attacks
          const userDetails = await api.auth.getCurrentUser();
          setUser(userDetails);
          safeSetItem('current_user', JSON.stringify(userDetails));
        } catch (error) {
          logger.error('Token validation failed, clearing session:', error);
          safeRemoveItem('access_token');
          safeRemoveItem('refresh_token');
          safeRemoveItem('current_user');
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  const login = async (username: string, password: string, tenantId?: number | null, tenantSlug?: string | null): Promise<TwoFARequired | void> => {
    try {
      const tokenResponse = await api.auth.login({ username, password, tenant_id: tenantId, tenant_slug: tenantSlug });

      // Check if 2FA is required
      if (tokenResponse.requires_2fa && tokenResponse.temp_token) {
        return {
          requires_2fa: true,
          temp_token: tokenResponse.temp_token,
          two_fa_method: tokenResponse.two_fa_method || 'email_otp',
        };
      }

      safeSetItem('access_token', tokenResponse.access_token);
      safeSetItem('refresh_token', tokenResponse.refresh_token);

      // Send device token if we have one stored (for "remember this device")
      const deviceToken = safeGetItem('device_token');
      if (deviceToken) {
        // The token was accepted without 2FA, device was remembered
      }

      // Get user details
      const userDetails = await api.auth.getCurrentUser();
      setUser(userDetails);
      safeSetItem('current_user', JSON.stringify(userDetails));
    } catch (error: any) {
      logger.error('Login failed:', error);
      if (error.response?.status === 429) {
        throw new Error('Too many login attempts. Please wait 1 minute before trying again.');
      }
      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  };

  const complete2FALogin = async (accessToken: string, refreshToken: string, deviceToken?: string) => {
    safeSetItem('access_token', accessToken);
    safeSetItem('refresh_token', refreshToken);

    if (deviceToken) {
      safeSetItem('device_token', deviceToken);
    }

    // Get user details with the new access token
    const userDetails = await api.auth.getCurrentUser();
    setUser(userDetails);
    safeSetItem('current_user', JSON.stringify(userDetails));
  };

  const logout = () => {
    api.auth.logout();
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    complete2FALogin,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
